/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */
import { FunctionComponent, VNode } from 'preact';

import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/pdp/components/Price/Price.css';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface PriceProps extends HTMLAttributes<HTMLDivElement> {
  price?: VNode;
  specialPrice?: VNode;
  tiers?: VNode;
}

export const Price: FunctionComponent<PriceProps> = ({
  className,
  price,
  specialPrice,
  tiers,
  children,
  ...props
}) => {
  const RegularPriceLabel = useText('PDP.Product.RegularPrice.label').label;
  const SpecialPriceLabel = useText('PDP.Product.SpecialPrice.label').label;
  const TierPriceLabel = useText('PDP.Product.TierPrice.label').label;

  return (
    <div {...props} className={classes(['pdp-price', className])}>
      {specialPrice && (
        <VComponent
          node={specialPrice}
          className={classes([
            'pdp-price__amount-special',
            'pdp-price__amount',
          ])}
          aria-label={SpecialPriceLabel}
          role="text"
        />
      )}
      {price && (
        <VComponent
          node={price}
          className={classes([
            'pdp-price__amount-regular',
            'pdp-price__amount--grey',
          ])}
          aria-label={RegularPriceLabel}
          role="text"
        />
      )}
      {tiers && (
        <VComponent
          node={tiers}
          className={classes(['pdp-price__tiers'])}
          aria-label={TierPriceLabel}
          role="text"
        />
      )}
    </div>
  );
};
